import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

const capModalTypes = ["co-cap-1", "co-cap-2", "co-cap-3", "co-cap-4", "co-cap-5", "co-cap-6"];

export default function FeaturesConsultoria({ onOpenModal }: { onOpenModal?: (type: string) => void }) {
  const { t } = useTranslation("common");
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const features = useMemo(() => [
    {
      icon: "ri-shield-check-line",
      title: t("co.features.f1.title"),
      description: t("co.features.f1.desc"),
      stat: t("co.features.f1.stat"),
      statLabel: t("co.features.f1.statLabel"),
    },
    {
      icon: "ri-search-eye-line",
      title: t("co.features.f2.title"),
      description: t("co.features.f2.desc"),
      stat: t("co.features.f2.stat"),
      statLabel: t("co.features.f2.statLabel"),
    },
    {
      icon: "ri-file-text-line",
      title: t("co.features.f3.title"),
      description: t("co.features.f3.desc"),
      stat: t("co.features.f3.stat"),
      statLabel: t("co.features.f3.statLabel"),
    },
    {
      icon: "ri-alert-line",
      title: t("co.features.f4.title"),
      description: t("co.features.f4.desc"),
      stat: t("co.features.f4.stat"),
      statLabel: t("co.features.f4.statLabel"),
    },
    {
      icon: "ri-restart-line",
      title: t("co.features.f5.title"),
      description: t("co.features.f5.desc"),
      stat: t("co.features.f5.stat"),
      statLabel: t("co.features.f5.statLabel"),
    },
    {
      icon: "ri-building-4-line",
      title: t("co.features.f6.title"),
      description: t("co.features.f6.desc"),
      stat: t("co.features.f6.stat"),
      statLabel: t("co.features.f6.statLabel"),
    },
  ], [t]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="features" className="relative py-16 md:py-24 bg-background-950 overflow-hidden">
      {/* Humanized background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Consulting%20team%20working%20together%20at%20a%20modern%20office%20table%20reviewing%20security%20strategy%2C%20warm%20human%20atmosphere%2C%20professional%20editorial%20photography%2C%20human%20centered%2C%20soft%20depth%20of%20field&width=900&height=506&seq=co-features-human-bg-01&orientation=landscape"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center opacity-[0.14]"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950 via-background-950/40 to-background-950" />
      </div>
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary-400/20 to-transparent" />

      <div className="w-full px-4 sm:px-6 lg:px-10 relative z-10">
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent-400/20 bg-accent-500/8 mb-4">
            <span className="text-[10px] font-label font-semibold text-accent-300 uppercase tracking-[0.2em]">
              {t("co.features.tag")}
            </span>
          </div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-50 mb-3 tracking-tight">
            {t("co.features.title")} <span className="text-accent-400">{t("co.features.highlight")}</span>
          </h2>
          <p className="text-sm text-foreground-400/60 max-w-lg mx-auto leading-relaxed">
            {t("co.features.subtitle")}
          </p>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature, idx) => (
            <button
              type="button"
              key={feature.title}
              onClick={() => onOpenModal?.(capModalTypes[idx])}
              className={`group relative text-left w-full cursor-pointer rounded-xl border border-foreground-100/8 bg-background-950/40 backdrop-blur-sm p-5 hover:border-primary-400/20 transition-all duration-500 hover:-translate-y-1 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${200 + idx * 100}ms` }}
            >
              <div className="absolute top-0 left-3 right-3 h-[2px] bg-gradient-to-r from-transparent via-primary-500/0 to-transparent group-hover:via-primary-500/50 transition-all duration-400 rounded-full" />

              <div className="relative z-10">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 border border-primary-400/15 text-primary-400 mb-4 group-hover:bg-primary-500/15 group-hover:text-primary-300 transition-all duration-300">
                  <i className={`${feature.icon} text-lg`} />
                </div>

                <div className="flex items-baseline gap-2 mb-2">
                  <h3 className="font-heading font-semibold text-base text-foreground-100 group-hover:text-primary-300 transition-colors">
                    {feature.title}
                  </h3>
                  <span className="text-[10px] font-label text-primary-400/60 opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-arrow-right-up-line" />
                  </span>
                </div>

                <p className="text-sm text-foreground-400/60 leading-relaxed mb-4">
                  {feature.description}
                </p>

                <div className="flex items-center gap-2 pt-3 border-t border-foreground-100/5">
                  <span className="font-heading font-bold text-sm text-primary-400">{feature.stat}</span>
                  <span className="text-[10px] font-label text-foreground-500/40 uppercase tracking-wider">{feature.statLabel}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}